import { Container, Label } from '@playcanvas/pcui';

import { Events } from '../events';
import { Splat } from '../splat';
import { localize, formatInteger } from './localization';
import { Tooltips } from './tooltips';

// Video memory, at the precision a status bar can use: MB below a gigabyte, GB above, one
// decimal place. Binary units, matching how graphics memory is universally reported.
const formatBytes = (bytes: number) => {
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) {
        return `${mb.toFixed(0)} MB`;
    }
    return `${(mb / 1024).toFixed(1)} GB`;
};

// The strip along the bottom of the dock: splat counts, telemetry and the busy indicator.
// The dock's panel toggles live in the tab row above it (ui/bottom-tabs.ts).
class StatusBar extends Container {
    constructor(events: Events, tooltips: Tooltips, args = {}) {
        args = {
            ...args,
            id: 'status-bar'
        };

        super(args);

        const statsContainer = new Container({
            class: 'status-bar-stats'
        });

        const createStat = (labelText: string) => {
            const container = new Container({
                class: 'status-bar-stat'
            });
            const label = new Label({
                class: 'status-bar-stat-label',
                text: labelText
            });
            const value = new Label({
                class: 'status-bar-stat-value',
                text: '0'
            });
            container.append(label);
            container.append(value);
            statsContainer.append(container);
            return value;
        };

        const splatsValue = createStat(localize('status-bar.splats'));
        const selectedValue = createStat(localize('status-bar.selected'));
        const lockedValue = createStat(localize('status-bar.locked'));
        const deletedValue = createStat(localize('status-bar.deleted'));
        const fpsValue = createStat(localize('status-bar.fps'));
        const vramValue = createStat(localize('status-bar.vram'));

        // Ready indicator: a dot plus a word, per section 36.1 of the specification. The dot
        // alone would be colour-only state, which the accessibility section rules out.
        const readyContainer = new Container({
            class: 'status-bar-ready'
        });
        const readyDot = new Container({ class: 'status-bar-ready-dot' });
        const readyLabel = new Label({
            class: 'status-bar-ready-label',
            text: localize('status-bar.ready')
        });
        readyContainer.append(readyDot);
        readyContainer.append(readyLabel);
        statsContainer.append(readyContainer);

        // A status light that can only ever say one thing is decoration, not status. The spinner
        // events are the app's existing "something long is happening" signal - imports, exports,
        // publishes and scene loads all raise them - so the indicator tracks them. They are
        // reference counted in ui/editor.ts, and this counts them the same way so that nested
        // operations do not clear the busy state early.
        let busyCount = 0;
        const setBusy = (busy: boolean) => {
            readyContainer.dom.classList[busy ? 'add' : 'remove']('busy');
            readyLabel.text = localize(busy ? 'status-bar.working' : 'status-bar.ready');
        };

        events.on('startSpinner', () => {
            if (++busyCount === 1) {
                setBusy(true);
            }
        });

        events.on('stopSpinner', () => {
            busyCount = Math.max(0, busyCount - 1);
            if (busyCount === 0) {
                setBusy(false);
            }
        });

        this.append(statsContainer);

        // Update stats from splat state
        let splat: Splat;

        const updateStats = () => {
            if (!splat) return;
            const state = splat.splatData.getProp('state') as Uint8Array;
            if (state) {
                splatsValue.text = formatInteger(state.length - splat.numDeleted);
                selectedValue.text = formatInteger(splat.numSelected);
                lockedValue.text = formatInteger(splat.numLocked);
                deletedValue.text = formatInteger(splat.numDeleted);
            }
        };

        events.on('splat.stateChanged', (splat_: Splat) => {
            splat = splat_;
            updateStats();
        });

        events.on('selection.changed', (selection: Element) => {
            if (selection instanceof Splat) {
                splat = selection;
                updateStats();
            }
        });

        events.on('telemetry', (data: { fps: number, vramBytes: number }) => {
            fpsValue.text = data.fps.toString();
            vramValue.text = formatBytes(data.vramBytes);
        });
    }
}

export { StatusBar };
